import {
  Input,
  MultiSearchForm,
  paymentTypeOptions,
  useDebouncedSearch,
  useEffect,
  useRef,
  useState,
} from "@";

import { searchValues } from "../utils/options";
import {
  getBankAccounts,
  getBanksLinksUsersCategoriesProjects,
} from "../utils/api";

const MultiSearch = ({ onSubmit, search, updateSearch, dataLength }) => {
  const [banks, setBanks] = useState([]);
  const hasFetchedData = useRef(false);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [affiliateUsers, setAffiliateUsers] = useState([]);
  const [affiliateLinks, setAffiliateLinks] = useState([]);

  // Debounce search input to avoid excessive API calls
  useDebouncedSearch(onSubmit, search.values, searchValues);

  // Fetch initial data for the form
  async function fetchAffiliateDonorsData() {
    try {
      const { banks, links, users, categories, projects } =
        await getBanksLinksUsersCategoriesProjects();
      setBanks(banks);
      setCategories(categories);
      setProjects(projects);
      setAffiliateUsers(users);
      setAffiliateLinks(links);
    } catch (error) {
      // Handle error
    }
  }

  //  fetching bank accounts based on bank ID
  const handleGetBankAccounts = async (id) => {
    if (!id) {
      setBankAccounts([]);
      return;
    }
    try {
      const { data } = await getBankAccounts(id);
      const accountsOptions = data?.map((option) => ({
        value: option.id,
        label: option.name,
      }));
      setBankAccounts(accountsOptions);
    } catch (error) {
      // Handle error
    }
  };

  const handleInputChange = (field, value) => {
    updateSearch({
      values: { ...search.values, [field]: value },
    });
  };

  // Fetch data when component mounts and dataLength is greater than 0
  useEffect(() => {
    if (dataLength > 0 && !hasFetchedData.current) {
      fetchAffiliateDonorsData();
      hasFetchedData.current = true;
    }
  }, [dataLength]);

  return (
    <>
      <MultiSearchForm
        show={search.show}
        onSubmit={onSubmit}
        className="multi-search-form xl:grid-cols-5"
        reset={() => updateSearch({ values: searchValues })}
      >
        {/* Input for keyword search */}
        <Input
          star
          label="كلمة البحث"
          value={search.values.title}
          change={(e) => handleInputChange("title", e.target.value)}
        />

        {/* Input for classification selection */}
        <Input
          star
          label="التصنيفات"
          type="select-searchable"
          list={categories}
          value={categories?.filter(
            (c) => search.values.classificationId == c.value,
          )}
          change={(e) =>
            handleInputChange("classificationId", e ? e.value : null)
          }
        />

        {/* Input for payment type selection */}
        <Input
          star
          type="select"
          label="طريقة الدفع"
          list={paymentTypeOptions}
          value={search.values.paymentType}
          change={(e) => handleInputChange("paymentType", e.target.value)}
        />

        {/* Input for payment status selection */}
        <Input
          star
          type="select"
          label="حالة الدفع"
          value={search.values.isPaid}
          list={[
            { value: true, name: "مدفوعة" },
            { value: false, name: "غير مدفوعة" },
          ]}
          change={(e) => handleInputChange("isPaid", e.target.value)}
        />

        {/* Input for project selection */}
        <Input
          star
          label="المشاريع"
          type="select-searchable"
          list={projects}
          value={projects?.filter((c) => search.values.project == c.value)}
          change={(e) => handleInputChange("project", e ? e.value : null)}
        />

        {/* Input for amount range selection */}
        <Input
          star
          type="number-range"
          labelA="المبلغ من"
          labelB="المبلغ الي"
          valueA={search.values.fromAmount}
          valueB={search.values.toAmount}
          changeA={(e) => handleInputChange("fromAmount", e.target.value)}
          changeB={(e) => handleInputChange("toAmount", e.target.value)}
        />

        {/* Input for date range selection */}
        <Input
          star
          showTimeSelect
          type="date-range"
          labelA="من تاريخ"
          labelB="الي تاريخ"
          style="lg:col-span-2"
          valueA={search.values.fromDate}
          valueB={search.values.toDate}
          changeA={(e) => handleInputChange("fromDate", e)}
          changeB={(e) => handleInputChange("toDate", e)}
        />

        {/* Input for affiliate user selection */}
        <Input
          star
          label="المنسقين"
          type="select-searchable"
          list={affiliateUsers}
          value={affiliateUsers?.filter(
            (c) => search.values.affiliateUser == c.value,
          )}
          change={(e) => handleInputChange("affiliateUser", e ? e.value : null)}
        />

        {/* Input for affiliate link selection */}
        <Input
          star
          label="الروابط"
          type="select-searchable"
          list={affiliateLinks}
          value={affiliateLinks?.filter(
            (c) => search.values.affiliateLink == c.value,
          )}
          change={(e) => handleInputChange("affiliateLink", e ? e.value : null)}
        />

        {/* Input for bank selection */}
        <Input
          star
          label="البنوك"
          type="select-searchable"
          value={banks?.filter((c) => search.values.bank == c.value)}
          list={banks}
          change={(e) => {
            handleGetBankAccounts(e.value);
            handleInputChange("bank", e ? e.value : null);
          }}
        />

        {/* Input for bank account selection */}
        <Input
          star
          label="الحساب"
          type="select-searchable"
          value={bankAccounts?.filter(
            (c) => search.values.bankAccount == c.value,
          )}
          list={bankAccounts}
          change={(e) => handleInputChange("bankAccount", e ? e.value : null)}
        />
      </MultiSearchForm>
    </>
  );
};

export default MultiSearch;
